/**
 * Desktop App Authorization Page (OAuth 2.0 Authorization Code + PKCE)
 *
 * Flow:
 * 1. Desktop app opens this page with state, code_challenge, client_id, redirect_uri
 * 2. User logs in (redirect to /auth if needed)
 * 3. User selects a room with a TRANSCRIPT service to write to
 * 4. Browser creates an authorization code via POST /api/desktop/authorize
 * 5. Code is passed to the desktop app via polygraf://callback?code=...&state=...
 * 6. Desktop app exchanges code for token via POST /api/desktop/token (back-channel)
 */

import { resolveApiBaseUrl } from '@polygraf/shared'
import {
	AlertCircle,
	Building,
	Check,
	ChevronRight,
	Copy,
	Eye,
	FileText,
	Loader2,
	Monitor,
	RefreshCw,
	Shield,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { trpc } from '@/trpc/client'
import { Button } from '../components/ui'
import { useSession } from '../lib/auth-client'

const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL, 'http://localhost:4000')
const PROTOCOL_REDIRECT_TIMEOUT_MS = 2000

/**
 * Defense-in-depth allowlist for the desktop app's redirect target. The server
 * is the authoritative validator (it checks redirect_uri against the registered
 * client), but the browser also refuses to hand the authorization code to
 * anything other than the custom app scheme or a loopback address — never an
 * arbitrary https origin — so a crafted `?redirect_uri=` cannot exfiltrate the
 * code to an attacker-controlled domain (RFC 8252, native-app best practice).
 */
function isAllowedDesktopRedirect(uri: string): boolean {
	if (uri.startsWith('polygraf://')) return true
	try {
		const u = new URL(uri)
		const isLoopback =
			u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '[::1]'
		return (u.protocol === 'http:' || u.protocol === 'https:') && isLoopback
	} catch {
		return false
	}
}

type Phase = 'loading' | 'select-room' | 'confirm' | 'authorizing' | 'success' | 'error'

interface WritableRoom {
	id: string
	name: string
	spaceId: string
	spaceName: string
	services: { id: string; name: string }[]
}

interface SelectedTarget {
	roomId: string
	roomName: string
	spaceName: string
	serviceId: string
	serviceName: string
}

export default function DesktopAuthPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { data: session, isPending: sessionLoading } = useSession()
	const [phase, setPhase] = useState<Phase>('loading')
	const [selected, setSelected] = useState<SelectedTarget | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [copied, setCopied] = useState(false)
	const [copyError, setCopyError] = useState(false)
	const [showFallback, setShowFallback] = useState(false)
	const [authCode, setAuthCode] = useState<string | null>(null)

	const params = new URLSearchParams(window.location.search)
	const state = params.get('state')
	const codeChallenge = params.get('code_challenge')
	const codeChallengeMethod = params.get('code_challenge_method')
	const clientId = params.get('client_id')
	const redirectUri = params.get('redirect_uri')

	const {
		data: writableRooms,
		isLoading: roomsLoading,
		isRefetching: roomsRefetching,
		refetch: refetchRooms,
		error: roomsError,
	} = trpc.rooms.listWritable.useQuery(undefined, {
		enabled: !!session?.user,
		retry: false,
	})

	// Redirect to login if not authenticated, validate required OAuth params
	useEffect(() => {
		if (sessionLoading) return
		if (!session?.user) {
			const returnTo = `/auth/desktop${window.location.search}`
			navigate(`/auth?returnTo=${encodeURIComponent(returnTo)}`, { replace: true })
			return
		}
		if (!state || state.length < 8 || state.length > 128) {
			setPhase('error')
			setError(t('desktopAuth.invalidState'))
			return
		}
		if (!codeChallenge || !codeChallengeMethod) {
			setPhase('error')
			setError(t('desktopAuth.missingPkce'))
			return
		}
		if (codeChallengeMethod !== 'S256') {
			setPhase('error')
			setError(t('desktopAuth.invalidChallengeMethod'))
			return
		}
		if (!clientId) {
			setPhase('error')
			setError(t('desktopAuth.missingClientId'))
			return
		}
		if (!redirectUri) {
			setPhase('error')
			setError(t('desktopAuth.missingRedirectUri'))
			return
		}
		if (!isAllowedDesktopRedirect(redirectUri)) {
			setPhase('error')
			setError(t('desktopAuth.invalidRedirect'))
			return
		}
		setPhase('select-room')
	}, [
		session,
		sessionLoading,
		state,
		codeChallenge,
		codeChallengeMethod,
		clientId,
		redirectUri,
		navigate,
		t,
	])

	const handleSelectService = (room: WritableRoom, service: { id: string; name: string }) => {
		setSelected({
			roomId: room.id,
			roomName: room.name,
			spaceName: room.spaceName,
			serviceId: service.id,
			serviceName: service.name,
		})
		setPhase('confirm')
	}

	const handleAuthorize = async () => {
		if (!selected || !codeChallenge || !codeChallengeMethod || !clientId || !redirectUri) return
		setPhase('authorizing')
		setError(null)

		try {
			const res = await fetch(`${API_URL}/api/desktop/authorize`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					code_challenge: codeChallenge,
					code_challenge_method: codeChallengeMethod,
					redirect_uri: redirectUri,
					client_id: clientId,
					serviceId: selected.serviceId,
					roomName: selected.roomName,
				}),
			})

			if (!res.ok) {
				const data = await res.json().catch(() => ({ error: 'Unknown error' }))
				// Surface the OAuth error_description (RFC 6749 §5.2) when present
				// so partner integrators can see what failed without rebuilding their
				// app — e.g. "Invalid redirect_uri" instead of just "invalid_request".
				const detail = data.error_description ? ` — ${data.error_description}` : ''
				throw new Error(`${data.error ?? `HTTP ${res.status}`}${detail}`)
			}

			const data = await res.json()
			setAuthCode(data.code)
			setPhase('success')

			// Redirect to desktop app with authorization code (NOT the token).
			// redirectUri was allowlisted on mount; re-check before navigating so
			// the code is never handed to an unexpected origin (defense in depth).
			if (isAllowedDesktopRedirect(redirectUri)) {
				const callbackUrl = `${redirectUri}?code=${encodeURIComponent(data.code)}&state=${encodeURIComponent(state ?? '')}`
				window.location.href = callbackUrl
			}

			setTimeout(() => setShowFallback(true), PROTOCOL_REDIRECT_TIMEOUT_MS)
		} catch (err) {
			setPhase('error')
			setError(err instanceof Error ? err.message : t('desktopAuth.authFailed'))
		}
	}

	const handleCopy = async () => {
		if (!authCode) return
		const payload = JSON.stringify({ code: authCode, state })
		try {
			await navigator.clipboard.writeText(payload)
			setCopied(true)
			setCopyError(false)
			setTimeout(() => setCopied(false), 3000)
		} catch {
			setCopyError(true)
		}
	}

	const handleCancel = () => {
		navigate('/')
	}

	const handleBack = () => {
		setSelected(null)
		setPhase('select-room')
	}

	// Group rooms by space for display
	const rooms = (writableRooms ?? []) as WritableRoom[]
	const roomsBySpace = rooms.reduce<Record<string, { spaceName: string; rooms: WritableRoom[] }>>(
		(acc, room) => {
			if (!acc[room.spaceId]) {
				acc[room.spaceId] = { spaceName: room.spaceName, rooms: [] }
			}
			acc[room.spaceId].rooms.push(room)
			return acc
		},
		{},
	)

	return (
		<div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
			<div className="card-refined w-full max-w-md p-8">
				{/* Header */}
				<div className="flex items-center justify-center mb-6">
					<div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
						<Monitor size={32} className="text-primary" />
					</div>
				</div>

				{/* Loading */}
				{(phase === 'loading' || sessionLoading) && (
					<div className="text-center" role="status" aria-live="polite">
						<Loader2 size={32} className="animate-spin mx-auto text-primary" />
						<p className="mt-4 text-base-content/60">{t('desktopAuth.loading')}</p>
					</div>
				)}

				{/* Room Selection */}
				{phase === 'select-room' && (
					<>
						<h1 className="text-xl font-bold text-center mb-2">{t('desktopAuth.selectRoom')}</h1>
						<p className="text-base-content/60 text-center text-sm mb-6">
							{t('desktopAuth.selectRoomDesc')}
						</p>

						{roomsLoading && (
							<div className="text-center py-8">
								<Loader2 size={24} className="animate-spin mx-auto text-primary" />
							</div>
						)}

						{/* A failed fetch must not masquerade as "no rooms assigned": retry:false
						    means one network blip lands here, and the noRooms copy would send the
						    user chasing the space owner instead of retrying or re-logging-in. */}
						{!roomsLoading && roomsError && (
							<div className="text-center py-8">
								<Building size={32} className="mx-auto text-coral-700/50 mb-3" />
								<p className="text-coral-700 text-sm">{t('desktopAuth.roomsLoadFailed')}</p>
								<p className="text-base-content/50 text-xs mt-1">{roomsError.message}</p>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => refetchRooms()}
									disabled={roomsRefetching}
									className="px-4 py-2 mt-4"
								>
									<RefreshCw size={16} className={roomsRefetching ? 'animate-spin' : undefined} />
									{t('desktopAuth.refreshRooms')}
								</Button>
							</div>
						)}

						{!roomsLoading && Object.keys(roomsBySpace).length === 0 && (
							<div className="text-center py-8">
								<Building size={32} className="mx-auto text-base-content/30 mb-3" />
								<p className="text-base-content/60 text-sm">{t('desktopAuth.noRooms')}</p>
								{/* Once an owner assigns the user, this re-runs the rooms query —
								    without it the page is a dead end until a full reload. */}
								<Button
									variant="ghost"
									size="sm"
									onClick={() => refetchRooms()}
									disabled={roomsRefetching}
									className="px-4 py-2 mt-4"
								>
									<RefreshCw size={16} className={roomsRefetching ? 'animate-spin' : undefined} />
									{t('desktopAuth.refreshRooms')}
								</Button>
							</div>
						)}

						{!roomsLoading &&
							Object.entries(roomsBySpace).map(([spaceId, { spaceName, rooms }]) => (
								<div key={spaceId} className="mb-4">
									<p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-2 px-1">
										{spaceName}
									</p>
									<div className="space-y-1">
										{rooms.map((room) =>
											room.services.map((service) => (
												<button
													key={service.id}
													onClick={() => handleSelectService(room, service)}
													aria-label={`${room.name} — ${service.name} — ${spaceName}`}
													className="w-full text-left bg-base-200 hover:bg-base-300 rounded-xl p-3 flex items-center gap-3 transition-colors"
												>
													<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
														<FileText size={18} className="text-primary" />
													</div>
													<div className="flex-1 min-w-0">
														<p className="font-medium text-sm truncate">{room.name}</p>
														<p className="text-xs text-base-content/50 truncate">{service.name}</p>
													</div>
													<ChevronRight size={16} className="text-base-content/30 flex-shrink-0" />
												</button>
											)),
										)}
									</div>
								</div>
							))}

						<Button variant="ghost" size="sm" block onClick={handleCancel} className="py-3 mt-4">
							{t('desktopAuth.cancel')}
						</Button>
					</>
				)}

				{/* Confirm */}
				{phase === 'confirm' && session?.user && selected && (
					<>
						<h1 className="text-xl font-bold text-center mb-2">{t('desktopAuth.title')}</h1>
						<p className="text-base-content/60 text-center text-sm mb-6">
							{t('desktopAuth.subtitle')}
						</p>

						{/* User info */}
						<div className="bg-base-200 rounded-xl p-4 mb-4">
							<p className="text-xs text-base-content/50 mb-1">{t('desktopAuth.authorizeAs')}</p>
							<p className="font-semibold">{session.user.name ?? session.user.email}</p>
							<p className="text-sm text-base-content/60">{session.user.email}</p>
						</div>

						{/* Selected room */}
						<div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-4">
							<p className="text-xs text-primary/70 mb-1">{t('desktopAuth.selectedRoom')}</p>
							<p className="font-semibold">{selected.roomName}</p>
							<p className="text-sm text-base-content/60">
								{selected.spaceName} · {selected.serviceName}
							</p>
						</div>

						{/* Permissions */}
						<div className="mb-6">
							<p className="text-sm font-medium mb-2">{t('desktopAuth.permissions')}</p>
							<ul className="space-y-2 text-sm text-base-content/70">
								<li className="flex items-center gap-2">
									<FileText size={16} className="text-primary flex-shrink-0" />
									{t('desktopAuth.permissionWrite')}
								</li>
								<li className="flex items-center gap-2">
									<Eye size={16} className="text-primary flex-shrink-0" />
									{t('desktopAuth.permissionRead')}
								</li>
							</ul>
						</div>

						{/* Actions */}
						<div className="flex gap-3">
							<Button variant="ghost" size="sm" onClick={handleBack} className="flex-1 py-3">
								{t('desktopAuth.back')}
							</Button>
							<Button onClick={handleAuthorize} className="flex-1">
								<Shield size={18} />
								{t('desktopAuth.authorize')}
							</Button>
						</div>
					</>
				)}

				{/* Authorizing */}
				{phase === 'authorizing' && (
					<div className="text-center" role="status" aria-live="polite">
						<Loader2 size={32} className="animate-spin mx-auto text-primary" />
						<p className="mt-4 font-medium">{t('desktopAuth.authorizing')}</p>
					</div>
				)}

				{/* Success */}
				{phase === 'success' && (
					<>
						<div className="text-center mb-6" role="status" aria-live="polite">
							<div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
								<Check size={24} className="text-success" />
							</div>
							<h2 className="text-lg font-bold">{t('desktopAuth.success')}</h2>
							<p className="text-sm text-base-content/60 mt-1">{t('desktopAuth.successDesc')}</p>
						</div>

						{/* Fallback: copy authorization code manually */}
						{showFallback && authCode && (
							<div className="bg-base-200 rounded-xl p-4">
								<p className="text-sm text-base-content/60 mb-3">{t('desktopAuth.fallback')}</p>
								<Button variant="ghost" size="sm" block onClick={handleCopy} className="py-2">
									{copied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
									{copied ? t('desktopAuth.tokenCopied') : t('desktopAuth.copyToken')}
								</Button>
								<span className="sr-only" role="status" aria-live="polite">
									{copied ? t('desktopAuth.tokenCopied') : ''}
								</span>
								{copyError && (
									<p role="alert" className="text-xs text-error mt-2 text-center">
										{t('desktopAuth.copyFailed')}
									</p>
								)}
							</div>
						)}
					</>
				)}

				{/* Error */}
				{phase === 'error' && (
					<div className="text-center" role="alert">
						<AlertCircle size={32} className="mx-auto text-error mb-3" />
						<h2 className="text-lg font-bold">{t('desktopAuth.error')}</h2>
						<p className="text-sm text-base-content/60 mt-1">{error}</p>
						<Button onClick={() => setPhase('select-room')} className="px-6 py-2 mt-4">
							{t('desktopAuth.errorRetry')}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
